import React, { useMemo } from 'react';
import cn from 'clsx';

import Collapsible from '../../library/collapsible/collapsible';
import CecContactUsChatButton from './components/cec-contact-us-chat-button';
import { PhoneSolidIcon } from '../../shared/icons/icons';
import CECHours from '../cec-hours/cec-hours';

import { useMediaQuery, useMyAccount } from '../../shared/hooks/hooks';
import useAemContent from '../../shared/hooks/use-aem-content/use-aem-content';

import { getCecPhoneNumber } from '../../shared/utils/cms/cms';
import { getIsCecAvailable } from '../../shared/utils/time/time';

import {
  AEM_CONTENT_PAGE_IDS,
  AEM_CONTENT_PARENT_IDS
} from '../../shared/constants/cms';

import styles from './cec-contact-us.scss';

enum CECContactUsTheme {
  GREY = 'grey'
}

enum CECContactUsOrientation {
  COLUMN = 'column',
  ROW = 'row'
}

interface CECContactUsProps {
  className?: string;
  hideNeedHelpText?: boolean;
  hidePhoneIcon?: boolean;
  isCollapsible?: boolean;
  isTires?: boolean;
  onToggle?: (isOpen?: boolean) => void;
  orientation?: CECContactUsOrientation;
  showChatButton?: boolean;
  theme?: CECContactUsTheme;
}

function CECContactUs(props: CECContactUsProps) {
  const {
    className = '',
    hideNeedHelpText = false,
    hidePhoneIcon = false,
    isCollapsible = false,
    isTires = true,
    onToggle,
    orientation = CECContactUsOrientation.COLUMN,
    showChatButton = false,
    theme
  } = props;
  const isGreyTheme = theme === CECContactUsTheme.GREY;

  const { aemContent, aemTooltipsContent } = useAemContent(
    AEM_CONTENT_PAGE_IDS.global
  );

  const cecPhoneNumberTitle =
    aemContent?.[AEM_CONTENT_PARENT_IDS.cecHelpPhoneContent]?.name;
  const { isFleetAccount } = useMyAccount();
  const featureOptions = {
    isFleetAccount,
    isTires
  };
  const { contactUsText, phoneNumber } = getCecPhoneNumber(
    aemTooltipsContent,
    featureOptions
  );

  const { isMinMd } = useMediaQuery();
  const isCecAvailable = useMemo(() => getIsCecAvailable(), []);

  if (!phoneNumber || !cecPhoneNumberTitle || !isCecAvailable) {
    return <></>;
  }

  return (
    <div
      className={cn(
        isGreyTheme
          ? styles[`container-grey-theme-${orientation}`]
          : styles[`container-${orientation}`],
        `cec-contact-us ${className}`
      )}
    >
      <div className={cn(styles['top-section'], 'cec-contact-us__top-section')}>
        {!hideNeedHelpText && (
          <span className={styles['text']}>{cecPhoneNumberTitle}</span>
        )}

        <div className={styles['contact-info']}>
          <span className={styles['text']}>{contactUsText}</span>
          {!isMinMd ? (
            <a href={`tel:${phoneNumber}`}>
              <PhoneSolidIcon className={styles['text']} />
              <strong>{phoneNumber}</strong>
            </a>
          ) : (
            <div>
              {!hidePhoneIcon && <PhoneSolidIcon className={styles['text']} />}
              <strong>{phoneNumber}</strong>
            </div>
          )}
        </div>
        {showChatButton && <CecContactUsChatButton />}
      </div>

      {isCollapsible ? (
        <Collapsible onToggle={onToggle} toggleType="CHEVRON">
          <div className={styles['collapsible-hours']}>
            <CECHours />
          </div>
        </Collapsible>
      ) : (
        <div
          className={cn(
            styles[`bottom-section-${orientation}`],
            'cec-contact-us__bottom-section'
          )}
        >
          <CECHours />
        </div>
      )}
    </div>
  );
}

export { CECContactUsOrientation, CECContactUsTheme };
export default CECContactUs;
